import React from 'react';
import { ChatMessage } from '@/api/chat';
import { User, Bot, ChevronDown, ChevronUp, Clock, Video, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MobileMessageBubbleProps {
  message: ChatMessage;
  isLast?: boolean;
  className?: string;
}

export default function MobileMessageBubble({ 
  message, 
  isLast = false,
  className 
}: MobileMessageBubbleProps) {
  const isUser = message.role === 'user';
  const [expanded, setExpanded] = React.useState(false);
  const [sourcesExpanded, setSourcesExpanded] = React.useState(false);

  // 格式化时间
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins}分钟前`;
    if (diffHours < 24) return `${diffHours}小时前`;
    if (diffDays < 7) return `${diffDays}天前`;
    
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' });
  };

  // 消息内容（可展开/收起）
  const maxLength = 300;
  const shouldTruncate = message.content.length > maxLength && !expanded;
  const displayContent = shouldTruncate 
    ? message.content.substring(0, maxLength) + '...' 
    : message.content;

  // 来源信息
  const hasSources = message.sources && message.sources.length > 0;

  return (
    <div className={cn(
      "group relative",
      isUser ? "flex justify-end" : "flex justify-start",
      className
    )}>
      {/* 消息容器 */}
      <div className={cn(
        "max-w-[90%] md:max-w-[80%]",
        isUser ? "ml-auto" : "mr-auto"
      )}>
        {/* 消息头 - 移动端更紧凑 */}
        <div className={cn(
          "flex items-center gap-1.5 mb-1.5 px-1",
          isUser ? "justify-end" : "justify-start"
        )}>
          <div className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
            isUser 
              ? "bg-blue-500 text-white" 
              : "bg-gradient-to-br from-green-500 to-emerald-600 text-white"
          )}>
            {isUser ? (
              <User className="w-3 h-3" />
            ) : (
              <Bot className="w-3 h-3" />
            )}
          </div>
          
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {isUser ? '我' : '张雪峰AI'}
          </span>
          
          <span className="text-xs text-gray-400 flex items-center gap-0.5">
            <Clock className="w-3 h-3" />
            {formatTime(message.createdAt)}
          </span>
        </div>

        {/* 消息内容 */}
        <div className={cn(
          "rounded-2xl px-3 py-2.5 relative",
          "touch-manipulation", // 优化移动端触摸
          isUser
            ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white"
            : "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200"
        )}>
          {/* 内容 */}
          <div className={cn(
            "text-sm md:text-base break-words leading-relaxed",
            isUser ? "text-white" : "text-gray-800 dark:text-gray-200"
          )}>
            {displayContent.split('\n').map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < message.content.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>

          {/* 展开/收起按钮 */}
          {message.content.length > maxLength && (
            <button
              onClick={() => setExpanded(!expanded)}
              className={cn(
                "mt-2 text-xs font-medium flex items-center gap-1",
                isUser 
                  ? "text-blue-200 hover:text-white" 
                  : "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
              )}
              aria-label={expanded ? "收起内容" : "展开内容"}
            >
              {expanded ? (
                <>
                  收起 <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  展开更多 <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          )}

          {/* 来源信息 */}
          {hasSources && (
            <div className="mt-2">
              <button
                onClick={() => setSourcesExpanded(!sourcesExpanded)}
                className={cn(
                  "w-full text-left flex items-center justify-between text-xs font-medium",
                  isUser
                    ? "text-blue-200 hover:text-white"
                    : "text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                )}
                aria-label={sourcesExpanded ? "收起来源" : "展开来源"}
              >
                <div className="flex items-center gap-1.5">
                  <Video className="w-3 h-3" />
                  <span>来源视频</span>
                </div>
                {sourcesExpanded ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>

              {sourcesExpanded && (
                <div className="mt-2 space-y-2">
                  {message.sources!.map((source, index) => (
                    <div
                      key={index}
                      className={cn(
                        "rounded-lg p-2 text-xs",
                        isUser
                          ? "bg-blue-400/20 text-blue-200"
                          : "bg-gray-200/50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400"
                      )}
                    >
                      <div className="font-medium mb-0.5">
                        {source.videoTitle || '张雪峰视频'}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        <span>{source.segmentText?.substring(0, 60)}...</span>
                      </div>
                      <div className="mt-1 text-[10px] opacity-70">
                        时间: {source.startTime} - {source.endTime}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 建议问题（仅AI消息） */}
        {!isUser && message.suggestedQuestions && message.suggestedQuestions.length > 0 && (
          <div className="mt-2 space-y-1.5">
            <div className="text-xs text-gray-500 dark:text-gray-400 px-1">
              建议追问:
            </div>
            <div className="flex flex-wrap gap-1.5">
              {message.suggestedQuestions.slice(0, 2).map((question, index) => (
                <button
                  key={index}
                  className="text-xs px-2.5 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors active:scale-95 touch-manipulation"
                  onClick={() => {
                    // 这里可以通过事件总线或回调传递给父组件
                    console.log('建议问题点击:', question);
                  }}
                  aria-label={`问：${question}`}
                >
                  {question.length > 20 ? question.substring(0, 20) + '...' : question}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 移动端手势提示（仅在最后一个AI消息显示） */}
      {!isUser && isLast && (
        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-400 animate-bounce flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          <span className="hidden sm:inline">长按消息可复制</span>
          <span className="sm:hidden">长按复制</span>
        </div>
      )}
    </div>
  );
}